// src/App.js
import React, { useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  Title
} from "chart.js";
import { GoogleMap, useJsApiLoader, DirectionsService, DirectionsRenderer } from "@react-google-maps/api";

// Register Chart.js components
Chart.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  Title
);

/* ------------------ CIRCUIT ICON SET (minimal, matches logo circuit motif) ------------------ */
function CircuitIcon({ variant }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (variant) {
    case "somm": // single node, signal point
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
        </svg>
      );
    case "itinerary": // routed path between two nodes
      return (
        <svg {...common}>
          <circle cx="5" cy="18" r="2" />
          <circle cx="19" cy="6" r="2" />
          <path d="M6.5 16.5 12 11 12 7 17.5 7.5" />
        </svg>
      );
    case "clients": // connected nodes / network
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="2.2" />
          <circle cx="17" cy="7" r="2.2" />
          <circle cx="12" cy="18" r="2.2" />
          <path d="M8.7 8.3 10.5 16.2M15.3 8.3 13.5 16.2M9.2 7h5.6" />
        </svg>
      );
    case "revenue": // stepped growth trace
      return (
        <svg {...common}>
          <path d="M3 17 8 17 8 12 13 12 13 7 18 7 18 4" />
          <circle cx="18" cy="4" r="1.8" />
        </svg>
      );
    default:
      return null;
  }
}

/* ------------------ CONFIGURATION ------------------ */
const GOOGLE_MAPS_API_KEY = "AIzaSyAGpKFlnYlHQCrX5l4j-woYYia4a5Gphxg";
const GOOGLE_LIBRARIES = ["places"];
const API_BASE_URL =
  process.env.NODE_ENV === "production" ? "" : "http://localhost:5005";

/* ------------------ STYLING & BRANDING ------------------ */
const colors = {
  navyBlue: "#001F3F",
  white: "#FFFFFF",
  silver: "#C0C0C0",
  darkGray: "#222222",
  lightBlue: "#ADD8E6",
  vividBlue: "#0277BD",
  red: "#FF0000",
  chartLightBlue: "#B3E5FC",
  chartMediumBlue: "#81D4FA",
};

const pageContainer = {
  minHeight: "100vh",
  backgroundImage: "url('/background.png')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  fontFamily: "Gotham, sans-serif",
  color: colors.white,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingTop: "min(20vh, 318px)",
  paddingBottom: "50px",
};

const logoStyle = {
  width: "100%",
  maxWidth: "280px",
  borderRadius: "20px",
  boxShadow: "0 16px 40px rgba(0,0,0,0.6)",
};

const dashboardNavStyle = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const navButtonStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "16px 20px",
  backgroundColor: "rgba(255,255,255,0.05)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: "1px solid rgba(192,192,192,0.18)",
  borderRadius: "14px",
  color: colors.white,
  fontSize: "15px",
  fontWeight: "600",
  letterSpacing: "0.01em",
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "Gotham, sans-serif",
  boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
};

const navIconStyle = {
  color: "#5FD4FF",
  filter: "drop-shadow(0 0 6px rgba(95,212,255,0.85)) drop-shadow(0 0 12px rgba(95,212,255,0.35))",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  flexShrink: 0,
};

const formContainerStyle = {
  width: "100%",
  maxWidth: "364px",
  boxSizing: "border-box",
  backgroundColor: "rgba(28,28,28,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  padding: "40px 24px",
  borderRadius: "20px",
  boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
  border: `1px solid rgba(192,192,192,0.18)`,
  textAlign: "center",
  marginTop: "40px",
  fontFamily: "Gotham, sans-serif",
};

const embeddedAiSommContainerStyle = {
  width: "100%",
  maxWidth: "600px",
  margin: "0 auto",
  backgroundColor: "rgba(28,28,28,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  padding: "40px",
  borderRadius: "20px",
  boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
  border: `1px solid rgba(192,192,192,0.18)`,
  textAlign: "center",
  boxSizing: "border-box",
  fontFamily: "Gotham, sans-serif",
};

const inputStyle = {
  width: "90%",
  padding: "14px 16px",
  marginBottom: "18px",
  borderRadius: "12px",
  border: `1px solid rgba(192,192,192,0.28)`,
  fontSize: "16px",
  backgroundColor: "rgba(255,255,255,0.05)",
  color: colors.white,
  boxSizing: "border-box",
  display: "block",
  marginLeft: "auto",
  marginRight: "auto",
  fontFamily: "Gotham, sans-serif",
  outline: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
};

const buttonStyle = {
  width: "90%",
  minWidth: "250px",
  padding: "14px",
  backgroundColor: colors.navyBlue,
  color: colors.white,
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: "12px",
  fontSize: "15px",
  fontWeight: "700",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  cursor: "pointer",
  whiteSpace: "nowrap",
  display: "block",
  margin: "0 auto",
  fontFamily: "Gotham Bold, sans-serif",
  boxShadow: "0 6px 18px rgba(0,31,63,0.55)",
  transition: "transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease",
};

const cardStyle = {
  width: "100%",
  maxWidth: "600px",
  boxSizing: "border-box",
  backgroundColor: "rgba(28,28,28,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  padding: "30px",
  borderRadius: "20px",
  boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
  textAlign: "center",
  marginBottom: "20px",
  color: colors.white,
  border: `1px solid rgba(192,192,192,0.18)`,
  fontFamily: "Gotham, sans-serif",
};

const headerStyle = {
  fontSize: "3rem",
  fontWeight: "600",
  marginBottom: "10px",
  textAlign: "center",
  color: colors.white,
  fontFamily: "Gotham Bold, sans-serif",
  letterSpacing: "-0.01em",
  textShadow: "0 2px 12px rgba(0,0,0,0.4)",
};

const backButtonStyle = {
  padding: "10px 18px",
  backgroundColor: "rgba(255,255,255,0.08)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  color: colors.white,
  border: "1px solid rgba(192,192,192,0.25)",
  borderRadius: "10px",
  fontSize: "14px",
  fontWeight: "600",
  cursor: "pointer",
  fontFamily: "Gotham Bold, sans-serif",
};

const animationVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const tripResponseBorderStyle = {
  border: "1px solid rgba(192,192,192,0.2)",
  borderRadius: "14px",
  padding: "18px",
  marginTop: "20px",
  minHeight: "150px",
  overflowY: "auto",
  maxHeight: "300px",
  textAlign: "left",
  fontFamily: "Gotham, sans-serif",
  backgroundColor: "rgba(255,255,255,0.03)",
};

const modifyButtonStyle = {
  padding: "10px 16px",
  backgroundColor: colors.navyBlue,
  color: colors.white,
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: "10px",
  fontSize: "14px",
  fontWeight: "700",
  cursor: "pointer",
  marginTop: "12px",
  fontFamily: "Gotham Bold, sans-serif",
  boxShadow: "0 4px 14px rgba(0,31,63,0.5)",
};

const sectionContainerStyle = {
  width: "100%",
  maxWidth: "800px",
  border: "1px solid rgba(192,192,192,0.18)",
  borderRadius: "20px",
  padding: "30px",
  marginBottom: "60px",
  backgroundColor: "rgba(28,28,28,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
  textAlign: "center",
  fontFamily: "Gotham, sans-serif",
};

const wowContainerSmall = {
  width: "100%",
  maxWidth: "335px",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  alignItems: "center",
  border: "1px solid rgba(192,192,192,0.18)",
  borderRadius: "20px",
  padding: "24px",
  backgroundColor: "rgba(28,28,28,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
  marginTop: "20px",
};

const wowContainerLarge = {
  width: "100%",
  maxWidth: "700px",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  alignItems: "center",
  border: "1px solid rgba(192,192,192,0.18)",
  borderRadius: "20px",
  padding: "32px",
  backgroundColor: "rgba(28,28,28,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
  marginTop: "20px",
};

/* ------------------ RESPONSIVE STYLES ------------------ */
function ResponsiveStyles() {
  return (
    <style>
      {`
      @media (max-width: 1024px) {
        .responsive-form,
        .responsive-container {
          padding: 20px !important;
        }
        .responsive-logo {
          width: 250px !important;
        }
      }
      @media (max-width: 768px) {
        .responsive-form {
          width: 95% !important;
          padding: 20px !important;
        }
        .responsive-container {
          padding-top: 100px !important;
        }
        .responsive-logo {
          width: 200px !important;
        }
        .chart-container {
          width: 100% !important;
          overflow-x: auto;
        }
      }
      @media (max-width: 480px) {
        .responsive-form {
          width: 100% !important;
          padding: 15px !important;
        }
        .responsive-logo {
          width: 150px !important;
        }
        .button {
          font-size: 14px !important;
          padding: 10px !important;
        }
      }
      /* Slick interactive states — layered on top of the existing palette, no color changes */
      input, select, textarea {
        appearance: none;
        -webkit-appearance: none;
      }
      select {
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='9' viewBox='0 0 14 9'><path d='M1 1l6 6 6-6' fill='none' stroke='%23C0C0C0' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
        background-repeat: no-repeat;
        background-position: right 16px center;
        padding-right: 40px !important;
      }
      input::placeholder, textarea::placeholder {
        color: rgba(255,255,255,0.45);
      }
      input:focus, select:focus, textarea:focus {
        border-color: rgba(255,60,60,0.55) !important;
        box-shadow: 0 0 0 3px rgba(255,0,0,0.14);
        background-color: rgba(255,255,255,0.08) !important;
      }
      input:hover, select:hover {
        border-color: rgba(192,192,192,0.45) !important;
      }
      button {
        transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
      }
      button:hover {
        transform: translateY(-2px);
        filter: brightness(1.12);
      }
      button:active {
        transform: translateY(0);
        filter: brightness(0.96);
      }
      /* Custom markdown styling */
      .markdown-content h2 {
        margin-top: 1em;
        margin-bottom: 0.5em;
      }
      .markdown-content h3 {
        margin-top: 0.75em;
        margin-bottom: 0.5em;
      }
      .markdown-content ul {
        margin-left: 1.5em;
        margin-bottom: 1em;
      }
      `}
    </style>
  );
}

/* ------------------ AnimatePresence Page Wrapper ------------------ */
function PageWrapper({ children }) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, y: -20 }}
      variants={animationVariant}
    >
      {children}
    </motion.div>
  );
}

/* ------------------ REGION DATA (IMPORTANT!) ------------------ */
const regionOptions = [
  { label: "Select a Wine Region", value: "" },
  { label: "Bordeaux (France)", value: "Bordeaux" },
  { label: "Burgundy (France)", value: "Burgundy" },
  { label: "Rhône Valley (France)", value: "Rhone" },
  { label: "Champagne (France)", value: "Champagne" },
  { label: "Tuscany (Italy)", value: "Tuscany" },
  { label: "Piedmont (Italy)", value: "Piemonte" },
  { label: "Ribera del Duero (Spain)", value: "Ribera del Duero" },
  { label: "Rioja (Spain)", value: "Rioja" },
  { label: "Douro Valley (Portugal)", value: "Douro Valley" },
  { label: "Mendoza (Argentina)", value: "Mendoza" },
  { label: "Napa Valley (USA)", value: "Napa Valley" },
  { label: "Willamette Valley (USA)", value: "Willamette Valley" },
  { label: "Other", value: "Other" },
];

/* ------------------ GOOGLE MAP COMPONENT ------------------ */
function DynamicGoogleMap({ markers, defaultZoom = 12, onMarkerHover }) {
  const containerStyle = { width: "100%", maxWidth: "600px", height: "300px" };
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_LIBRARIES,
  });
  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState(null);
  const [directionsRequested, setDirectionsRequested] = useState(false);
  const markersRef = useRef([]);

  const effectiveMarkers =
    markers && markers.length > 0
      ? markers
      : [
          {
            latitude: 49.0439,
            longitude: 3.9586,
            location: "Dummy Vineyard",
            rating: "4.5",
          },
          {
            latitude: 49.004,
            longitude: 3.9563,
            location: "Dummy Hotel",
            rating: "4.0",
          },
        ];

  const computedCenter = effectiveMarkers.reduce(
    (acc, marker, _, arr) => {
      acc.lat += Number(marker.latitude);
      acc.lng += Number(marker.longitude);
      if (arr.length) {
        acc.lat /= arr.length;
        acc.lng /= arr.length;
      }
      return acc;
    },
    { lat: 0, lng: 0 }
  );

  const directionsOptions =
    effectiveMarkers.length >= 2
      ? {
          origin: {
            lat: Number(effectiveMarkers[0].latitude),
            lng: Number(effectiveMarkers[0].longitude),
          },
          destination: {
            lat: Number(effectiveMarkers[effectiveMarkers.length - 1].latitude),
            lng: Number(effectiveMarkers[effectiveMarkers.length - 1].longitude),
          },
          waypoints: effectiveMarkers.slice(1, -1).map((m) => ({
            location: { lat: Number(m.latitude), lng: Number(m.longitude) },
            stopover: true,
          })),
          travelMode: "DRIVING",
        }
      : null;

  const handleDirectionsResult = (result, status) => {
    setDirectionsRequested(true);
    if (status === "OK") {
      setDirections(result);
    } else {
      console.error("Directions request failed:", status, "— check that Directions API is enabled for this key in Google Cloud Console.");
    }
  };

  const onLoad = (mapInstance) => {
    setMap(mapInstance);
    const bounds = new window.google.maps.LatLngBounds();
    effectiveMarkers.forEach((marker) => {
      const lat = Number(marker.latitude);
      const lng = Number(marker.longitude);
      const position = new window.google.maps.LatLng(lat, lng);
      bounds.extend(position);

      const markerInstance = new window.google.maps.Marker({
        position,
        map: mapInstance,
        title: marker.location,
      });
      markerInstance.addListener("mouseover", () => {
        if (onMarkerHover) onMarkerHover(marker);
      });
      markersRef.current.push(markerInstance);
    });
    mapInstance.fitBounds(bounds);
  };

  const onUnmount = () => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    setMap(null);
  };

  if (loadError) {
    console.error("Error loading Google Maps API:", loadError);
    return <div>Error loading map</div>;
  }
  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={computedCenter}
      zoom={defaultZoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {directionsOptions && !directionsRequested && (
        <DirectionsService options={directionsOptions} callback={handleDirectionsResult} />
      )}
      {directions && (
        <DirectionsRenderer
          options={{
            directions,
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: "#5FD4FF",
              strokeWeight: 4,
              strokeOpacity: 0.85,
            },
          }}
        />
      )}
    </GoogleMap>
  ) : (
    <div>Loading Map...</div>
  );
}

/* ------------------ AI SOMM MODULE ------------------ */
function AiSomm({ onModifyTrip, embedded, bare }) {
  const [askQuery, setAskQuery] = useState("");
  const [askAnswer, setAskAnswer] = useState(null);

  const handleInputChange = (e) => {
    setAskQuery(e.target.value);
  };

  async function fetchAgentResponse(query, mode = "general") {
    const res = await fetch(`${API_BASE_URL}/api/agent/ask`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, mode }),
    });
    const responseData = await res.json();
    if (responseData.error) throw new Error(responseData.error);
    return responseData.answer;
  }

  const handleAsk = async () => {
    if (!askQuery.trim()) return;
    setAskAnswer("Loading...");
    try {
      const answer = await fetchAgentResponse(askQuery, "general");
      setAskAnswer(answer);
    } catch (error) {
      console.error("Error in handleAsk:", error);
      setAskAnswer("Sorry, something went wrong.");
    }
  };

  const handleModifyTrip = async () => {
    const modificationQuery = prompt(
      "Enter modification details (e.g., rearrange Day 2 with Day 3):"
    );
    if (!modificationQuery) return;
    const combinedPrompt = `Current itinerary details: ${onModifyTrip.currentItinerary}\n\nModification request: ${modificationQuery}`;
    try {
      const res = await fetch(`${API_BASE_URL}/api/agent/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: combinedPrompt, mode: "itinerary" }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      onModifyTrip.currentItineraryData.details = data.answer;
      setAskAnswer("Itinerary modified successfully.");
    } catch (error) {
      console.error("Error modifying itinerary:", error);
      setAskAnswer("Sorry, something went wrong during modification.");
    }
  };

  const featureLogoStyle = {
    width: bare ? "168px" : "150px",
    borderRadius: "16px",
    boxShadow: "0 10px 28px rgba(0,0,0,0.55)",
    marginBottom: "14px",
  };

  const sectionTitleStyle = {
    fontSize: "1.8rem",
    fontWeight: "600",
    marginBottom: "15px",
    textAlign: "center",
    color: colors.white,
    fontFamily: "Gotham Bold, sans-serif",
  };

  const smallButtonStyle = {
    padding: "12px 18px",
    backgroundColor: colors.navyBlue,
    color: colors.white,
    border: "1px solid rgba(255,255,255,0.14)",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    fontFamily: "Gotham Bold, sans-serif",
    boxShadow: "0 4px 14px rgba(0,31,63,0.5)",
  };

  const placeholderText = embedded
    ? "e.g., Is Bordeaux cold in September?"
    : "e.g., Is Champagne close to Paris?";

  const containerStyle = bare
    ? { width: "100%", textAlign: "center" }
    : embedded
    ? embeddedAiSommContainerStyle
    : formContainerStyle;

  return (
    <div style={containerStyle}>
      <motion.img
        src="/AISomm.jpg"
        alt="AI Somm™ Logo"
        style={featureLogoStyle}
        initial="hidden"
        animate="visible"
        variants={animationVariant}
      />
      <h2 style={sectionTitleStyle}>AI Somm™</h2>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "10px",
        }}
      >
        <input
          type="text"
          name="askQuery"
          value={askQuery}
          onChange={handleInputChange}
          style={{ ...inputStyle, marginBottom: 0 }}
          placeholder={placeholderText}
        />
        <motion.button
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
          onClick={handleAsk}
          style={smallButtonStyle}
        >
          Ask
        </motion.button>
      </div>
      {askAnswer && (
        <div style={tripResponseBorderStyle}>
          <ReactMarkdown>{askAnswer}</ReactMarkdown>
          {onModifyTrip && (
            <button onClick={handleModifyTrip} style={modifyButtonStyle}>
              Modify Trip
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------ AI SOMM PAGE with PageWrapper ------------------ */
function AiSommPage() {
  return (
    <PageWrapper>
      <div className="responsive-container" style={pageContainer}>
        <ResponsiveStyles />
        <ScreenCard title="Virtual Sommelier">
          <AiSomm bare />
        </ScreenCard>
      </div>
    </PageWrapper>
  );
}

/* ------------------ HEADER COMPONENT ------------------ */
function HeaderSub({ showBack }) {
  const navigate = useNavigate();
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: showBack ? "space-between" : "center",
        padding: "10px 0",
      }}
    >
      {showBack && (
        <motion.button
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
          style={backButtonStyle}
          onClick={() => navigate("/dashboard")}
        >
          ← Back
        </motion.button>
      )}
      <div style={{ flexGrow: 1 }}></div>
      <motion.img
        src="/DionysusAI.jpg"
        alt="DionysusAI Logo"
        style={{
          width: "100%",
          maxWidth: "130px",
          borderRadius: "14px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
        }}
        initial="hidden"
        animate="visible"
        variants={animationVariant}
      />
    </div>
  );
}

/* ------------------ SCREEN CARD — single iPhone-friendly card: back + logo header row + title + content ------------------ */
function ScreenCard({ title, children, maxWidth = "420px" }) {
  const navigate = useNavigate();
  return (
    <div style={{ ...wowContainerSmall, maxWidth, boxSizing: "border-box", alignItems: "stretch", padding: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <motion.button
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
          style={{ ...backButtonStyle, padding: "8px 14px", fontSize: "13px" }}
          onClick={() => navigate("/dashboard")}
        >
          ← Back
        </motion.button>
        <img
          src="/DionysusAI.jpg"
          alt="Dionysus AI"
          style={{
            width: "44px",
            height: "44px",
            borderRadius: "10px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.5)",
            objectFit: "cover",
          }}
        />
      </div>
      <h1
        style={{
          ...headerStyle,
          fontSize: "1.7rem",
          margin: "18px 0 4px",
        }}
      >
        {title}
      </h1>
      {children}
    </div>
  );
}

/* ------------------ LOGIN PAGE with PageWrapper ------------------ */
function LoginPage({ setAuth }) {
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setAuth(true);
    navigate("/dashboard");
  };

  return (
    <PageWrapper>
      <div className="responsive-container" style={pageContainer}>
        <ResponsiveStyles />
        <motion.div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
          initial="hidden"
          animate="visible"
          variants={animationVariant}
        >
          <div style={wowContainerLarge}>
            <motion.img
              src="/DionysusAI.jpg"
              alt="DionysusAI Logo"
              style={logoStyle}
              initial="hidden"
              animate="visible"
              variants={animationVariant}
            />
            <form onSubmit={handleSubmit} style={{ width: "100%", marginTop: "8px" }} className="responsive-form">
              <input
                type="email"
                placeholder="User ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                style={inputStyle}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
                required
              />
              <motion.button
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
                type="submit"
                style={buttonStyle}
              >
                Login
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    </PageWrapper>
  );
}

/* ------------------ DASHBOARD with PageWrapper ------------------ */
function Dashboard() {
  const navigate = useNavigate();
  return (
    <PageWrapper>
      <div className="responsive-container" style={pageContainer}>
        <ResponsiveStyles />
        <div style={{ ...wowContainerSmall, padding: "28px 24px" }}>
          <motion.img
            src="/DionysusAI.jpg"
            alt="DionysusAI Logo"
            style={logoStyle}
            initial="hidden"
            animate="visible"
            variants={animationVariant}
          />
          <div style={dashboardNavStyle}>
            <motion.button
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              style={navButtonStyle}
              onClick={() => navigate("/ai-somm")}
            >
              <span style={navIconStyle}><CircuitIcon variant="somm" /></span> AI Somm™
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              style={navButtonStyle}
              onClick={() => navigate("/itinerary-planner")}
            >
              <span style={navIconStyle}><CircuitIcon variant="itinerary" /></span> Wine Travel Itinerary
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              style={navButtonStyle}
              onClick={() => navigate("/clients")}
            >
              <span style={navIconStyle}><CircuitIcon variant="clients" /></span> Customer Insights
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              style={navButtonStyle}
              onClick={() => navigate("/insights")}
            >
              <span style={navIconStyle}><CircuitIcon variant="revenue" /></span> Revenue Analytics
            </motion.button>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

/* ------------------ INSIGHTS with PageWrapper + Enhanced Tooltips ------------------ */
function Insights() {
  const monthlyBookingOptions = {
    responsive: true,
    plugins: {
      tooltip: {
        backgroundColor: "rgba(255,255,255,0.2)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#fff",
        borderWidth: 1,
      },
    },
  };

  const monthlyBookingData = {
    labels: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
    datasets: [
      {
        label: "2024 Deposits",
        data: [0, 0, 0, 0, 0, 6, 0, 0, 0, 6, 0, 0],
        borderColor: "#81D4FA",
        backgroundColor: "#B3E5FC",
        fill: false,
      },
      {
        label: "2025 Deposits",
        data: [8, 8, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        borderColor: "#0277BD",
        backgroundColor: "#0288D1",
        fill: false,
      },
    ],
  };

  const groupSizeData = {
    labels: ["2023", "2025"],
    datasets: [
      {
        label: "Avg. Group Size",
        data: [6, 12],
        backgroundColor: ["#81D4FA", "#0277BD"],
      },
    ],
  };

  const conversionData = {
    labels: ["2024", "2025"],
    datasets: [
      {
        label: "Conversion Rate (%)",
        data: [20, 60],
        backgroundColor: ["#B3E5FC", "#0288D1"],
      },
    ],
  };

  return (
    <PageWrapper>
      <div className="responsive-container" style={pageContainer}>
        <ResponsiveStyles />
        <ScreenCard title="Revenue Analytics">
          <div
            style={{
              ...cardStyle,
              width: "100%",
              boxSizing: "border-box",
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <h2
              style={{
                fontSize: "1.4rem",
                marginBottom: "15px",
                textAlign: "center",
                fontFamily: "Gotham Bold, sans-serif",
              }}
            >
              Booking Comparison
            </h2>
            <div
              className="chart-container"
              style={{
                width: "100%",
                maxWidth: "100%",
                margin: "0 auto",
                textAlign: "center",
              }}
            >
              <Line data={monthlyBookingData} options={monthlyBookingOptions} />
            </div>
          </div>
          <div
            style={{
              ...cardStyle,
              width: "100%",
              boxSizing: "border-box",
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <h2
              style={{
                fontSize: "1.4rem",
                marginBottom: "15px",
                textAlign: "center",
                fontFamily: "Gotham Bold, sans-serif",
              }}
            >
              Historic Group Size
            </h2>
            <div
              className="chart-container"
              style={{
                width: "100%",
                maxWidth: "100%",
                margin: "0 auto",
                textAlign: "center",
              }}
            >
              <Bar data={groupSizeData} />
            </div>
          </div>
          <div
            style={{
              ...cardStyle,
              width: "100%",
              boxSizing: "border-box",
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <h2
              style={{
                fontSize: "1.4rem",
                marginBottom: "15px",
                textAlign: "center",
                fontFamily: "Gotham Bold, sans-serif",
              }}
            >
              Conversion Rate
            </h2>
            <div
              className="chart-container"
              style={{
                width: "100%",
                maxWidth: "100%",
                margin: "0 auto",
                textAlign: "center",
              }}
            >
              <Bar data={conversionData} />
            </div>
          </div>
        </ScreenCard>
      </div>
    </PageWrapper>
  );
}

/* ------------------ CLIENTS with PageWrapper ------------------ */
function Clients() {
  const clientCardStyle = {
    border: "1px solid rgba(192,192,192,0.18)",
    borderRadius: "16px",
    padding: "22px",
    marginBottom: "16px",
    width: "100%",
    boxSizing: "border-box",
    boxShadow: "0 8px 28px rgba(0,0,0,0.45)",
    backgroundColor: "rgba(28,28,28,0.82)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "Gotham, sans-serif",
  };

  const clientsData = [
    {
      name: "Jill Miller",
      recommendation:
        "Recommended an exclusive September Bordeaux River Cruise featuring private tastings and gourmet dining—building on her memorable Tuscan wine adventure.",
    },
    {
      name: "Jim Jones",
      recommendation:
        "Recommended a Loire Valley wine and castle excursion as part of his Paris itinerary, merging historical elegance with premium tastings.",
    },
    {
      name: "Jenny Sanders",
      recommendation:
        "Recommended a bespoke Porto culinary & wine pre-trip ahead of her Douro Valley River Cruise in October, crafted for discerning travelers.",
    },
  ];

  const handleSendEmail = (clientName) => {
    alert(`Operator triggered: Email sent to ${clientName} with the AI recommendation.`);
  };

  return (
    <PageWrapper>
      <div className="responsive-container" style={pageContainer}>
        <ResponsiveStyles />
        <ScreenCard title="Smart Client Insights">
          {clientsData.map((client, index) => (
            <div key={index} style={clientCardStyle}>
              <p
                style={{
                  fontSize: "1.2rem",
                  marginBottom: "10px",
                  textAlign: "center",
                  fontFamily: "Gotham Bold, sans-serif",
                }}
              >
                <strong>{client.name}</strong>
              </p>
              <p style={{ marginBottom: "20px", textAlign: "center" }}>
                {client.recommendation}
              </p>
              <motion.button
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
                style={buttonStyle}
                onClick={() => handleSendEmail(client.name)}
              >
                Send Email
              </motion.button>
            </div>
          ))}
        </ScreenCard>
      </div>
    </PageWrapper>
  );
}

/* ------------------ ITINERARY PLANNER with PageWrapper ------------------ */
function ItineraryPlanner() {
  const [itinerary, setItinerary] = useState(null);
  const [formData, setFormData] = useState({
    region: "",
    otherRegion: "",
    days: "",
    travelers: "",
    dates: "",
    interests: "",
    budget: "",
    pace: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const buildPrompt = () => {
    const region = formData.region === "Other" ? formData.otherRegion : formData.region;
    const { days, travelers, dates, interests, budget, pace } = formData;
    return `Generate a detailed itinerary for a ${budget} wine tour in ${region} lasting ${days} days for ${travelers} travelers during ${dates}. The itinerary must be formatted in polished Markdown with clear headings and bullet lists, and should NOT include raw coordinates. Return a JSON object with keys "answer" (the itinerary text) and "markers" (an array of marker objects with numeric 'latitude', 'longitude', 'location', 'type', and optionally 'rating').`;
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    try {
      const prompt = buildPrompt();
      const res = await fetch(`${API_BASE_URL}/api/agent/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: prompt, mode: "itinerary" }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      const parsed = JSON.parse(data.answer);
      let mapCenter = { lat: 44.8378, lng: -0.5792 };

      if (parsed.markers && parsed.markers.length > 0) {
        const avg = parsed.markers.reduce(
          (acc, m) => {
            acc.lat += Number(m.latitude);
            acc.lng += Number(m.longitude);
            return acc;
          },
          { lat: 0, lng: 0 }
        );
        mapCenter = {
          lat: avg.lat / parsed.markers.length,
          lng: avg.lng / parsed.markers.length,
        };
      }
      setItinerary({
        title: "Your Proposed Itinerary",
        details: parsed.answer,
        markers: parsed.markers,
        mapCenter,
      });
    } catch (error) {
      console.error("Error generating itinerary:", error);
    }
    setIsLoading(false);
  };

  const handleModifiedTrip = async () => {
    if (!itinerary) return;
    const modificationQuery = prompt(
      "Enter modification details (e.g., rearrange Day 2 with Day 3):"
    );
    if (!modificationQuery) return;
    const combinedPrompt = `Current itinerary details: ${itinerary.details}\n\nModification request: ${modificationQuery}`;
    try {
      const res = await fetch(`${API_BASE_URL}/api/agent/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: combinedPrompt, mode: "itinerary" }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setItinerary({
        ...itinerary,
        details: data.answer,
      });
    } catch (error) {
      console.error("Error modifying itinerary:", error);
    }
  };

  const handleExecuteItinerary = () => {
    if (!itinerary) return;
    alert("Mock email sent with the itinerary details.");
  };

  const handleDownloadPDF = () => {
    alert("Simulating PDF download...");
  };

  return (
    <PageWrapper>
      <div className="responsive-container" style={pageContainer}>
        <ResponsiveStyles />
        <ScreenCard title="Itinerary Planner">
        {!itinerary ? (
            <div className="responsive-form" style={{ width: "100%" }}>
              <h2
                style={{
                  fontSize: "1.8rem",
                  marginBottom: "15px",
                  textAlign: "center",
                  fontFamily: "Gotham Bold, sans-serif",
                }}
              >
                Enter Trip Details
              </h2>
              <select
                name="region"
                value={formData.region}
                onChange={handleChange}
                style={inputStyle}
              >
                {regionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {formData.region === "Other" && (
                <input
                  type="text"
                  name="otherRegion"
                  placeholder="Enter custom region"
                  style={inputStyle}
                  value={formData.otherRegion}
                  onChange={handleChange}
                />
              )}
              <input
                type="number"
                name="days"
                placeholder="Number of Days"
                style={inputStyle}
                value={formData.days}
                onChange={handleChange}
              />
              <input
                type="number"
                name="travelers"
                placeholder="Number of Travelers"
                style={inputStyle}
                value={formData.travelers}
                onChange={handleChange}
              />
              <input
                type="text"
                name="dates"
                placeholder="Travel Dates or Season"
                style={inputStyle}
                value={formData.dates}
                onChange={handleChange}
              />
              <input
                type="text"
                name="interests"
                placeholder='Interests (e.g., "local cuisine", "vineyards")'
                style={inputStyle}
                value={formData.interests}
                onChange={handleChange}
              />
              <input
                type="text"
                name="budget"
                placeholder='Budget (e.g., "luxury" or "moderate")'
                style={inputStyle}
                value={formData.budget}
                onChange={handleChange}
              />
              <input
                type="text"
                name="pace"
                placeholder='Trip Pace ("relaxing" or "activity-packed")'
                style={inputStyle}
                value={formData.pace}
                onChange={handleChange}
              />
              <motion.button
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
                onClick={handleGenerate}
                style={buttonStyle}
                disabled={isLoading}
              >
                {isLoading ? "Generating..." : "Generate Itinerary"}
              </motion.button>
            </div>
          ) : (
            <div style={{ width: "100%" }}>
              <h2
                style={{
                  fontSize: "2rem",
                  marginBottom: "15px",
                  textAlign: "center",
                  fontFamily: "Gotham Bold, sans-serif",
                }}
              >
                {itinerary.title}
              </h2>
              <div style={tripResponseBorderStyle}>
                <ReactMarkdown>{itinerary.details}</ReactMarkdown>
              </div>
              <h3
                style={{
                  fontSize: "1.6rem",
                  margin: "20px 0 10px",
                  textAlign: "center",
                  fontFamily: "Gotham Bold, sans-serif",
                }}
              >
                Recommended Locations Map
              </h3>
              <DynamicGoogleMap markers={itinerary.markers} defaultZoom={12} />
              <div style={{ marginTop: "20px" }}>
                <AiSomm
                  onModifyTrip={{
                    currentItinerary: itinerary.details,
                    currentItineraryData: itinerary,
                  }}
                  embedded={true}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "20px",
                  gap: "10px",
                  width: "100%",
                }}
              >
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                  style={{ ...buttonStyle, minWidth: "120px" }}
                  onClick={handleDownloadPDF}
                >
                  Download PDF
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    ...buttonStyle,
                    minWidth: "120px",
                    backgroundColor: colors.red,
                  }}
                  onClick={handleExecuteItinerary}
                >
                  Execute Itinerary
                </motion.button>
              </div>
            </div>
          )}
        </ScreenCard>
      </div>
    </PageWrapper>
  );
}

/* ------------------ MAIN APP COMPONENT w/ AnimatePresence ------------------ */
function App() {
  const [isAuthenticated, setAuth] = useState(false);
  const location = useLocation(); // for AnimatePresence route transitions

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Wrap pages in PageWrapper to animate route changes */}
        <Route
          path="/login"
          element={
            <PageWrapper>
              <LoginPage setAuth={setAuth} />
            </PageWrapper>
          }
        />
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              <Dashboard />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/itinerary-planner"
          element={
            isAuthenticated ? (
              <ItineraryPlanner />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/ai-somm"
          element={
            isAuthenticated ? (
              <AiSommPage />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/insights"
          element={
            isAuthenticated ? (
              <Insights />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/clients"
          element={
            isAuthenticated ? (
              <Clients />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </AnimatePresence>
  );
}

/* We typically do <Router><App/></Router> in index.js, but if everything is in one file: */
export default function RootApp() {
  return (
    <Router>
      <App />
    </Router>
  );
}